import React from "react";
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Storage as StorageIcon,
  AccountTree as FlowChartIcon,
  Description as DocumentIcon,
  DataObject as DatabaseIcon,
  Email as EmailIcon,
  RequestPage as RequestIcon,
  Assignment as ActionLogIcon,
  Feedback as FeedbackIcon,
  Terminal as TerminalIcon,
  Settings as SettingsIcon,
  LocalShipping as ShipmentIcon,
  AutoFixHigh as AutomationIcon,
  Checklist as TestSetIcon,
  Category as CategoryIcon,
  Widgets as WidgetsIcon,
  Security as SecurityIcon,
  Flag as FlagIcon,
} from "@mui/icons-material";

export interface AdminPageConfig {
  /** Unique key for the page */
  key: string;
  /** Display title */
  title: string;
  /** Short title for app bar (optional, defaults to title) */
  shortTitle?: string;
  /** Description for dashboard */
  description: string;
  /** Icon component */
  icon: React.ReactNode;
  /** Navigation path */
  path: string;
  /** Whether to show in app bar quick links */
  showInAppBar?: boolean;
  /** Whether to show in dashboard (default: true) */
  showInDashboard?: boolean;
}

/**
 * All admin pages configuration.
 * This is the single source of truth for admin navigation.
 */
export const adminPagesConfig: AdminPageConfig[] = [
  {
    key: "workflows",
    title: "Workflows",
    description: "Manage workflows and recurring tasks",
    icon: <FlowChartIcon fontSize="small" />,
    path: "/admin/workflows",
    showInAppBar: true,
  },
  {
    key: "users",
    title: "Users Management",
    shortTitle: "Users",
    description:
      "View all users, manage email connections, and assign organizations",
    icon: <PeopleIcon fontSize="small" />,
    path: "/admin/list_users",
    showInAppBar: true,
  },
  {
    key: "action-logs",
    title: "Action Logs",
    description:
      "View system action logs for workflows, emails, and GPT requests",
    icon: <ActionLogIcon fontSize="small" />,
    path: "/admin/action-logs",
    showInAppBar: true,
  },
  {
    key: "shipment-monitor",
    title: "Shipment Dashboard Monitor",
    shortTitle: "Shipment Monitor",
    description:
      "View and manage shipment feeds organized from email processing",
    icon: <ShipmentIcon fontSize="small" />,
    path: "/admin/shipments-dashboard-monitor",
    showInAppBar: true,
  },
  {
    key: "email-search",
    title: "Email Tools",
    shortTitle: "Email",
    description: "Search emails, view threads, and manage email data",
    icon: <EmailIcon fontSize="small" />,
    path: "/admin/email/search",
    showInAppBar: true,
  },
  {
    key: "commands",
    title: "Commands",
    description: "Execute admin commands for system management and operations",
    icon: <TerminalIcon fontSize="small" />,
    path: "/admin/commands",
    showInAppBar: true,
  },
  {
    key: "configs",
    title: "System Configuration",
    shortTitle: "Configs",
    description: "Manage system-wide configuration settings and defaults",
    icon: <SettingsIcon fontSize="small" />,
    path: "/admin/configs",
    showInAppBar: true,
  },
  {
    key: "email-automation",
    title: "Email Automation Pipeline",
    shortTitle: "Email Automation",
    description:
      "Configure and run email automation pipelines with categorization, extraction, and presentation",
    icon: <AutomationIcon fontSize="small" />,
    path: "/admin/email-automation",
    showInAppBar: true,
  },
  {
    key: "test-sets",
    title: "Test Sets",
    shortTitle: "Test Sets",
    description: "Manage test sets for pipeline testing",
    icon: <TestSetIcon fontSize="small" />,
    path: "/admin/test-sets",
    showInAppBar: true,
  },
  {
    key: "feedback",
    title: "User Feedback",
    description: "View and manage user feedback about email routing issues",
    icon: <FeedbackIcon fontSize="small" />,
    path: "/admin/feedback",
    showInAppBar: true,
  },
  {
    key: "requests",
    title: "LLM Requests",
    description: "View and analyze ChatGPT/LLM requests and responses",
    icon: <RequestIcon fontSize="small" />,
    path: "/admin/requests",
    showInAppBar: true,
  },
  {
    key: "reminders",
    title: "Reminders & Actions",
    shortTitle: "Reminders & Actions",
    description: "View and manage shipment exceptions and reminders dashboard",
    icon: <ShipmentIcon fontSize="small" />,
    path: "/admin/reminders",
    showInAppBar: true,
  },

  {
    key: "permission-groups",
    title: "Permission Groups",
    shortTitle: "Permissions",
    description: "Manage permission groups and access control rules",
    icon: <SecurityIcon fontSize="small" />,
    path: "/admin/permission-groups",
  },
  {
    key: "feature-flags",
    title: "Feature Flags",
    shortTitle: "Flags",
    description: "Manage feature flags for controlled feature rollout",
    icon: <FlagIcon fontSize="small" />,
    path: "/admin/feature-flags",
    showInAppBar: true,
  },

  // === Dashboard-only Pages (showInAppBar: false or undefined) ===
  {
    key: "organizations",
    title: "Organizations",
    description:
      "Create, edit, and delete organizations. Manage organization members",
    icon: <BusinessIcon fontSize="small" />,
    path: "/admin/organizations",
  },
  {
    key: "extracted-data",
    title: "Extracted Data",
    description:
      "Manage organization data entries. Create, view, and edit extracted data",
    icon: <StorageIcon fontSize="small" />,
    path: "/admin/extracted-data",
  },

  {
    key: "docs",
    title: "Documents",
    description: "Browse and manage system documents",
    icon: <DocumentIcon fontSize="small" />,
    path: "/admin/docs",
  },
  {
    key: "database",
    title: "Database Editor",
    description: "Direct database access and editing tools",
    icon: <DatabaseIcon fontSize="small" />,
    path: "/admin/database",
    showInDashboard: false,
  },
  {
    key: "shipment-email-category-validation",
    title: "Validate Shipment Email Categories",
    description:
      "Validate and recategorize shipment emails by tag, view and edit category definitions",
    icon: <CategoryIcon fontSize="small" />,
    path: "/admin/shipment-email-category-validation",
  },
  {
    key: "msoffice",
    title: "Mock Outlook Plugin",
    shortTitle: "MS Office",
    description: "Test and preview the Outlook add-in plugin interface",
    icon: <WidgetsIcon fontSize="small" />,
    path: "/admin/msoffice",
    showInAppBar: true,
  },
];

/**
 * Get pages that should appear in the app bar (in array order)
 */
export function getAppBarPages(): AdminPageConfig[] {
  return adminPagesConfig.filter((page) => page.showInAppBar);
}

/**
 * Get pages for the dashboard (excludes pages with showInDashboard: false)
 */
export function getDashboardPages(): AdminPageConfig[] {
  return adminPagesConfig.filter((page) => page.showInDashboard !== false);
}
